import type { SimplifiedNotification } from './types'

export const notificationUtils = {
  // Filtering
  filterUnread: (notifications: SimplifiedNotification[]) => notifications.filter((notification) => !notification.read),

  filterByType: (notifications: SimplifiedNotification[], type: 'engagement' | 'request' | 'support' | 'system') =>
    notifications.filter((notification) => notification.type === type),

  // Sorting
  sortByDate: (notifications: SimplifiedNotification[]) =>
    notifications.sort((a, b) => {
      const aTime = a.createdAt.toDate?.() || new Date(a.createdAt.seconds * 1000)
      const bTime = b.createdAt.toDate?.() || new Date(b.createdAt.seconds * 1000)
      return bTime.getTime() - aTime.getTime()
    }),

  // Counting
  getUnreadCount: (notifications: SimplifiedNotification[]) =>
    notifications.filter((notification) => !notification.read).length,

  getCountByType: (notifications: SimplifiedNotification[], type: 'engagement' | 'request' | 'support' | 'system') =>
    notifications.filter((notification) => notification.type === type).length,

  // Grouping
  groupByType: (notifications: SimplifiedNotification[]) => {
    return notifications.reduce(
      (groups, notification) => {
        const type = notification.type
        if (!groups[type]) {
          groups[type] = []
        }
        groups[type].push(notification)
        return groups
      },
      {} as Record<'engagement' | 'request' | 'support' | 'system', SimplifiedNotification[]>,
    )
  },

  // Complex operations
  getEngagementNotifications: (notifications: SimplifiedNotification[]) =>
    notifications.filter((notification) => notification.type === 'engagement'),

  getRequestNotifications: (notifications: SimplifiedNotification[]) =>
    notifications.filter((notification) => notification.type === 'request'),

  // Group engagement notifications by post
  groupEngagementsByPost: (notifications: SimplifiedNotification[]) => {
    const engagementNotifications = notifications.filter((n) => n.type === 'engagement')
    const grouped = new Map<string, SimplifiedNotification[]>()

    for (const notification of engagementNotifications) {
      const postId = notification.data?.postId
      if (postId) {
        if (!grouped.has(postId)) {
          grouped.set(postId, [])
        }
        grouped.get(postId)!.push(notification)
      }
    }

    // Convert to grouped notifications with combined messages
    const result: SimplifiedNotification[] = []

    for (const [postId, postNotifications] of grouped) {
      if (postNotifications.length === 1) {
        // Single notification, keep as is
        result.push(postNotifications[0])
      } else {
        // Multiple notifications, create a grouped one
        const latest = postNotifications[0] // Already sorted by date
        const commentCount = postNotifications.filter((n) => n.subtype === 'post_comment').length
        const likeCount = postNotifications.filter((n) => n.subtype === 'post_like').length

        // Generate Instagram-style message
        let message = ''
        const postTitle = latest.data?.postTitle || 'post'

        if (commentCount > 0 && likeCount > 0) {
          // Both comments and likes
          const names = Array.from(new Set(postNotifications.map((n) => n.data?.senderName).filter(Boolean)))
          if (names.length === 1) {
            message = `${names[0]} commented and liked your post "${postTitle}"`
          } else if (names.length === 2) {
            message = `${names[0]}, ${names[1]} and ${postNotifications.length - 2} other${postNotifications.length - 2 > 1 ? 's' : ''} interacted with your post "${postTitle}"`
          } else {
            message = `${names[0]}, ${names[1]} and ${postNotifications.length - 2} others interacted with your post "${postTitle}"`
          }
        } else if (commentCount > 0) {
          // Only comments
          const commenters = postNotifications
            .filter((n) => n.subtype === 'post_comment')
            .map((n) => n.data?.senderName)
            .filter(Boolean) as string[]
          const uniqueCommenters = Array.from(new Set(commenters))

          if (commentCount === 1) {
            message = `${uniqueCommenters[0]} commented on your post "${postTitle}"`
          } else if (uniqueCommenters.length === 1) {
            message = `${uniqueCommenters[0]} commented ${commentCount} times on your post "${postTitle}"`
          } else if (uniqueCommenters.length === 2) {
            message = `${uniqueCommenters[0]} and ${uniqueCommenters[1]} commented on your post "${postTitle}"`
          } else {
            const othersCount = uniqueCommenters.length - 1
            message = `${uniqueCommenters[0]} and ${othersCount} other${othersCount > 1 ? 's' : ''} commented on your post "${postTitle}"`
          }
        } else if (likeCount > 0) {
          // Only likes
          const likers = postNotifications
            .filter((n) => n.subtype === 'post_like')
            .map((n) => n.data?.senderName)
            .filter(Boolean) as string[]
          const uniqueLikers = Array.from(new Set(likers))

          if (likeCount === 1) {
            message = `${uniqueLikers[0]} liked your post "${postTitle}"`
          } else if (uniqueLikers.length === 1) {
            message = `${uniqueLikers[0]} liked your post "${postTitle}"`
          } else if (uniqueLikers.length === 2) {
            message = `${uniqueLikers[0]} and ${uniqueLikers[1]} liked your post "${postTitle}"`
          } else {
            const othersCount = uniqueLikers.length - 1
            message = `${uniqueLikers[0]} and ${othersCount} other${othersCount > 1 ? 's' : ''} liked your post "${postTitle}"`
          }
        }

        const groupedNotification: SimplifiedNotification = {
          ...latest,
          id: `grouped-${postId}`,
          message,
          read: postNotifications.every((n) => n.read), // Read only if all are read
          data: {
            ...latest.data,
            postId, // Ensure postId is available for click navigation
          },
        }

        result.push(groupedNotification)
      }
    }

    return result
  },
}
